import React, { useEffect, useMemo, useState } from 'react';
import aiAutomationApi from '../../utils/aiAutomationApi';
import { useTheme } from '../../contexts/ThemeContext';

const AdminWorkflowDocumentsPage = () => {
  const { isDark } = useTheme();
  const [documents, setDocuments] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState('');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDocument, setLoadingDocument] = useState(false);
  const [error, setError] = useState('');

  const selectedMeta = useMemo(
    () => documents.find((doc) => doc.slug === selectedSlug) || null,
    [documents, selectedSlug]
  );

  useEffect(() => {
    const loadDocuments = async () => {
      setLoadingList(true);
      setError('');
      try {
        const response = await aiAutomationApi.get('/admin/workflow-documents');
        const rows = response?.documents || [];
        setDocuments(rows);
        if (rows.length > 0) {
          setSelectedSlug(rows[0].slug);
        }
      } catch (err) {
        setError(err?.message || 'Failed to load workflow documents.');
      } finally {
        setLoadingList(false);
      }
    };

    loadDocuments();
  }, []);

  useEffect(() => {
    if (!selectedSlug) return;

    const loadDocument = async () => {
      setLoadingDocument(true);
      setError('');
      try {
        const response = await aiAutomationApi.get(`/admin/workflow-documents/${encodeURIComponent(selectedSlug)}`);
        setSelectedDocument(response?.document || null);
      } catch (err) {
        setError(err?.message || 'Failed to load workflow document.');
        setSelectedDocument(null);
      } finally {
        setLoadingDocument(false);
      }
    };

    loadDocument();
  }, [selectedSlug]);

  const handleDownload = () => {
    if (!selectedDocument?.content) return;
    const blob = new Blob([selectedDocument.content], { type: selectedDocument.mime_type || 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = selectedDocument.download_name || selectedDocument.file_name || `${selectedDocument.slug}.md`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={`min-h-full ${isDark ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-900'}`}>
      <div className="px-8 py-7 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Workflow Documents</h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              View and download internal workflow references.
            </p>
          </div>
          <button
            type="button"
            onClick={handleDownload}
            disabled={!selectedDocument?.content}
            className="inline-flex items-center rounded-md border border-gray-300 dark:border-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Download
          </button>
        </div>
      </div>

      <div className="grid grid-cols-[320px_1fr] min-h-[calc(100vh-185px)]">
        <aside className="border-r border-gray-200 dark:border-gray-800 p-5">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
            Documents
          </div>

          {loadingList ? (
            <div className="text-sm text-gray-500 dark:text-gray-400">Loading documents...</div>
          ) : documents.length === 0 ? (
            <div className="text-sm text-gray-500 dark:text-gray-400">No workflow documents found.</div>
          ) : (
            <div className="space-y-2">
              {documents.map((doc) => {
                const active = doc.slug === selectedSlug;
                return (
                  <button
                    key={doc.slug}
                    type="button"
                    onClick={() => setSelectedSlug(doc.slug)}
                    className={`w-full text-left rounded-md border px-3 py-3 transition-colors ${
                      active
                        ? 'border-indigo-300 bg-indigo-50 text-indigo-950 dark:border-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-100'
                        : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800'
                    }`}
                  >
                    <div className="text-sm font-semibold">{doc.title}</div>
                    <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{doc.category}</div>
                  </button>
                );
              })}
            </div>
          )}
        </aside>

        <main className="p-6">
          {error && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/70 dark:bg-red-950/30 dark:text-red-300">
              {error}
            </div>
          )}

          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold">
                {selectedDocument?.title || selectedMeta?.title || 'Workflow Document'}
              </h2>
              {(selectedDocument?.description || selectedMeta?.description) && (
                <p className="mt-1 max-w-3xl text-sm text-gray-500 dark:text-gray-400">
                  {selectedDocument?.description || selectedMeta?.description}
                </p>
              )}
            </div>
            {(selectedDocument?.updated_at || selectedMeta?.updated_at) && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Updated {new Date(selectedDocument?.updated_at || selectedMeta?.updated_at).toLocaleString()}
              </div>
            )}
          </div>

          <div className="rounded-md border border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-950">
            {loadingDocument ? (
              <div className="p-6 text-sm text-gray-500 dark:text-gray-400">Loading document...</div>
            ) : selectedDocument?.content ? (
              <pre className="max-h-[calc(100vh-275px)] overflow-auto whitespace-pre-wrap break-words p-6 text-sm leading-6 text-gray-800 dark:text-gray-200">
                {selectedDocument.content}
              </pre>
            ) : (
              <div className="p-6 text-sm text-gray-500 dark:text-gray-400">Select a document to view.</div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminWorkflowDocumentsPage;
